import React, { useEffect, useRef, useState } from "react";
import { Box, Portal, Text, VStack } from "@chakra-ui/react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  arrow,
} from "@floating-ui/react-dom";

const POPOVER_WIDTH = 280;
const TARGET_GAP = 12;
const VIEWPORT_PADDING = 8;
const ARROW_BOX = 12;
const MIN_POPOVER_HEIGHT = 120;
const STATIC_SIDE_BY_PLACEMENT = {
  top: "bottom",
  bottom: "top",
  left: "right",
  right: "left",
};
const TARGET_ZINDEX_DEFAULT = "1350";
const TARGET_ZINDEX_ABOVE_MODAL = "1450";
const MAX_HEIGHT_VAR = "--demo-guide-popover-max-height";

const useTargetElement = (selector, isOpen) => {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    if (!isOpen || !selector) {
      setTarget(null);
      return undefined;
    }

    const existing = document.querySelector(selector);
    if (existing) {
      setTarget(existing);
      return undefined;
    }

    const observer = new MutationObserver(() => {
      const found = document.querySelector(selector);
      if (found) {
        setTarget(found);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [selector, isOpen]);

  return target;
};

const useHasRenderedGeometry = (target) => {
  const [hasGeometry, setHasGeometry] = useState(false);

  useEffect(() => {
    if (!target) {
      setHasGeometry(false);
      return undefined;
    }

    const measure = () => {
      const { width, height } = target.getBoundingClientRect();
      setHasGeometry(width > 0 && height > 0);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(target);

    return () => observer.disconnect();
  }, [target]);

  return hasGeometry;
};

const useScrollTargetIntoView = (target, enabled) => {
  useEffect(() => {
    if (!target || !enabled) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        observer.disconnect();
        if (entry.intersectionRatio < 1) {
          target.scrollIntoView({ block: "center", behavior: "smooth" });
        }
      },
      { threshold: [0, 1] }
    );
    observer.observe(target);

    return () => observer.disconnect();
  }, [target, enabled]);
};

const useElevatedTarget = (target, aboveModal) => {
  useEffect(() => {
    if (!target) return undefined;

    const previousPosition = target.style.position;
    const previousZIndex = target.style.zIndex;

    // z-index only applies to positioned elements, so a static target needs a
    // positioning context before it can rise above the backdrop.
    if (window.getComputedStyle(target).position === "static") {
      target.style.position = "relative";
    }
    target.style.zIndex = aboveModal
      ? TARGET_ZINDEX_ABOVE_MODAL
      : TARGET_ZINDEX_DEFAULT;

    return () => {
      target.style.position = previousPosition;
      target.style.zIndex = previousZIndex;
    };
  }, [target, aboveModal]);
};

/**
 * DemoGuidePopover — popover anchored to a target element resolved by selector.
 * Used for joyride-style steps (WELCOME near Start Agenda, GUIDE_STOP near Stop button).
 * Highlights the target element above the backdrop so users can click it.
 */
const DemoGuidePopover = ({ isOpen, stepConfig, onEndDemo }) => {
  const arrowRef = useRef(null);
  const targetSelector = stepConfig?.targetSelector;
  const isAboveModal = Boolean(stepConfig?.aboveModal);

  const target = useTargetElement(targetSelector, isOpen);
  const hasGeometry = useHasRenderedGeometry(target);
  const anchor = hasGeometry ? target : null;

  useScrollTargetIntoView(target, hasGeometry);
  useElevatedTarget(anchor, isAboveModal);

  const { refs, floatingStyles, middlewareData, placement } = useFloating({
    placement: "bottom",
    // Fixed strategy keeps overflow detection anchored to the viewport; with the
    // default absolute strategy a scrollable ancestor becomes the boundary and
    // the popover can be clamped off-screen.
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(TARGET_GAP),
      // Sideways fallbacks matter on short viewports: with only top/bottom the
      // popover has nowhere to go and ends up covering the very control the
      // step is asking the user to click.
      flip({
        padding: VIEWPORT_PADDING,
        fallbackPlacements: ["top", "right", "left"],
      }),
      shift({ padding: VIEWPORT_PADDING }),
      size({
        padding: VIEWPORT_PADDING,
        apply({ availableHeight, elements }) {
          elements.floating.style.setProperty(
            MAX_HEIGHT_VAR,
            `${Math.max(MIN_POPOVER_HEIGHT, availableHeight)}px`
          );
        },
      }),
      arrow({ element: arrowRef, padding: ARROW_BOX }),
    ],
  });

  useEffect(() => {
    refs.setReference(anchor);
  }, [refs, anchor]);

  if (!isOpen || !anchor) return null;

  const side = placement.split("-")[0];
  const staticSide = STATIC_SIDE_BY_PLACEMENT[side];
  const { x: arrowX, y: arrowY } = middlewareData.arrow || {};

  return (
    <Portal>
      {/* Backdrop overlay — skip when aboveModal since Chakra modal already provides one */}
      {!isAboveModal && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.400"
          zIndex={1300}
          pointerEvents="none"
        />
      )}

      <Box
        ref={refs.setFloating}
        data-testid="demo-guide-popover"
        data-placement={side}
        style={floatingStyles}
        zIndex={isAboveModal ? 1500 : 1400}
        // Guide text is informational and can overlap controls the step is
        // asking the user to click (including an open modal's own buttons), so
        // it must never swallow clicks — only End Demo opts back in.
        pointerEvents="none"
        bg="white"
        borderRadius="12px"
        boxShadow="lg"
        p={4}
        maxW={`${POPOVER_WIDTH}px`}
        w={`${POPOVER_WIDTH}px`}
        fontFamily="Geist, sans-serif"
      >
        <Box
          ref={arrowRef}
          position="absolute"
          w={`${ARROW_BOX}px`}
          h={`${ARROW_BOX}px`}
          bg="white"
          transform="rotate(45deg)"
          left={arrowX != null ? `${arrowX}px` : undefined}
          top={arrowY != null ? `${arrowY}px` : undefined}
          {...{ [staticSide]: `${-ARROW_BOX / 2}px` }}
        />

        <VStack
          spacing={2}
          align="start"
          maxH={`var(${MAX_HEIGHT_VAR})`}
          overflowY="auto"
        >
          {stepConfig?.heading && (
            <Text fontSize="sm" fontWeight="700" color="#0C001C">
              {stepConfig.heading}
            </Text>
          )}
          {stepConfig?.body && (
            <Text fontSize="xs" color="#6B7280" lineHeight="1.5">
              {stepConfig.body}
            </Text>
          )}
          <Text
            fontSize="xs"
            color="gray.400"
            cursor="pointer"
            pointerEvents="auto"
            _hover={{ color: "gray.600" }}
            onClick={onEndDemo}
            pt={1}
          >
            End Demo
          </Text>
        </VStack>
      </Box>
    </Portal>
  );
};

export default DemoGuidePopover;
